import { useState } from "react";

import { Eye, Lock } from "lucide-react";

import { StarButton } from "@/components/ui/StarButton";
import { cn } from "@/lib/utils";
import { stageMeta } from "@/lib/domain-constants";
import {
  formatMoney,
  formatDate,
  formatMonth,
  displayedAmount,
} from "@/lib/format";
import { useViewQuota } from "@/hooks/queries/useViewQuota";
import { useContractingRow } from "./useContractingRow";
import {
  NewBadge,
  MinorBadge,
  SubjectPill,
  stageClass,
  RegistrationEnd,
  FROM_BASES_HINT,
} from "./pills";
import { pillText, highlight, VISIBLE_MATCHES } from "./MatchPill";
import { QualificationBadge } from "./QualificationBadge";
import {
  GHOST_NOMENCLATURES,
  GHOST_DESCRIPTIONS,
  GHOST_LOCATIONS,
  ghostPick,
} from "@/lib/ghost";
import type { ContractingItem, MatchedInterest } from "@/types/domain";

/**
 * Ítem coincidente dentro de la tarjeta. Va sin hover: en táctil no existe, y el
 * detalle (cantidad, precio unitario) vive en la vista de la licitación.
 */
function MatchRow({
  match,
  isMinor,
  currencySymbol,
}: {
  match: MatchedInterest;
  isMinor: boolean;
  currencySymbol: string;
}) {
  return (
    <div className="flex items-start gap-1.5 text-[11px] font-semibold leading-tight text-emerald-700 dark:text-emerald-400">
      <span className="mt-1 size-1 shrink-0 rounded-full bg-emerald-500" />
      <span className="min-w-0 flex-1 break-words">
        {highlight(
          pillText(match.description, match.keyword, isMinor),
          match.keyword,
        )}
      </span>
      <span className="shrink-0 tabular-nums">
        {match.amount > 0
          ? formatMoney(match.amount, currencySymbol)
          : `${currencySymbol} -`}
      </span>
    </div>
  );
}

/** Cuántos desbloqueos quedan este mes; se muestra bajo las tarjetas bloqueadas. */
function RemainingUnlocks() {
  const { data: quota } = useViewQuota();
  if (!quota || quota.unlimited || quota.remaining <= 0) return null;
  return (
    <p className="text-[11px] text-muted-foreground">
      Te quedan {quota.remaining} desbloqueos este mes.
    </p>
  );
}

/**
 * La licitación del listado en formato tarjeta, para móvil y tablet. La tabla
 * necesita 960px de ancho; debajo de `lg` se pinta esto.
 */
export function ContractingCard({
  c,
  onView,
}: {
  c: ContractingItem;
  onView?: (c: ContractingItem) => void;
}) {
  const {
    locked,
    currencySymbol,
    amount,
    matches,
    stage,
    daysContratado,
    registrationEndsAt,
    saved,
    untrackedCount,
    hasUntracked,
    starDisabled,
    starTitle,
    onStarClick,
  } = useContractingRow(c);
  const [showAllMatches, setShowAllMatches] = useState(false);

  const matchTotal = matches.reduce((sum, m) => sum + (m.amount ?? 0), 0);
  const location = locked
    ? ghostPick(c.contracting_id, GHOST_LOCATIONS)
    : [c.province?.province_name, c.district?.district_name]
        .filter(Boolean)
        .join(" · ");

  return (
    <div
      onClick={() => onView?.(c)}
      className="flex flex-col gap-3 rounded-2xl bg-card p-4 shadow-sm"
    >
      <div className="flex items-start justify-between gap-3">
        {/* Data fantasma: texto plausible difuminado. select-none + aria-hidden
            para que no sea seleccionable ni leído por lectores de pantalla. */}
        {locked ? (
          <div className="min-w-0 flex-1 select-none" aria-hidden="true">
            {c.isNew && <NewBadge className="mb-1.5 inline-block" />}
            {c.contracting_minorid && (
              <MinorBadge className="mb-1.5 inline-block" />
            )}
            <p className="truncate text-xs font-bold text-slate-400 blur-[4px]">
              {ghostPick(c.contracting_id, GHOST_NOMENCLATURES)}
            </p>
            <p className="mt-1.5 line-clamp-3 text-xs leading-relaxed text-slate-300 blur-[4px]">
              {ghostPick(c.contracting_id, GHOST_DESCRIPTIONS)}
            </p>
          </div>
        ) : (
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              {c.isNew && <NewBadge />}
              {c.contracting_minorid && <MinorBadge />}
              <p className="min-w-0 break-all text-xs font-bold">
                {c.contracting_nomenclature}
              </p>
            </div>
            <p className="mt-1.5 line-clamp-3 text-xs leading-relaxed text-muted-foreground">
              {c.contracting_description}
            </p>
            {c.contracting_entity && (
              <p className="mt-1 truncate text-[11px] text-muted-foreground/70">
                {c.contracting_entity}
              </p>
            )}
          </div>
        )}

        {locked ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(c);
            }}
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-2 text-[11px] font-bold text-primary transition-colors active:bg-primary/20"
          >
            <Lock className="size-3.5" />
            Ver detalle
          </button>
        ) : (
          <div className="flex shrink-0 flex-col gap-2">
            <StarButton
              saved={saved}
              onClick={onStarClick}
              disabled={starDisabled}
              title={starTitle}
              badge={hasUntracked ? untrackedCount : undefined}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onView?.(c);
              }}
              title="Ver detalle"
              className="flex size-8 items-center justify-center rounded-full border bg-card text-slate-600 transition-colors active:bg-accent/30 dark:text-slate-300"
            >
              <Eye className="size-4" />
            </button>
          </div>
        )}
      </div>

      {/* Objeto · etapa · dónde y cuándo. La fecha exacta y la provincia/distrito
          son data bloqueada: sin desbloquear van difuminadas o sin el día. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[11px] text-muted-foreground">
        <SubjectPill subject={c.contracting_contractsubject} />
        <span
          className={cn(
            "rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
            stageClass(stage),
          )}
        >
          {stageMeta(stage).label}
          {daysContratado !== null && (
            <span className="ml-1 font-semibold normal-case tracking-normal opacity-80">
              {daysContratado}d
            </span>
          )}
        </span>
        {registrationEndsAt && <RegistrationEnd endsAt={registrationEndsAt} />}
        <span className="flex min-w-0 items-center gap-1 uppercase">
          <span className="truncate font-medium">
            {c.department?.department_name || "-"}
          </span>
          {location && (
            <span
              className={cn("truncate", locked && "select-none blur-[3px]")}
              aria-hidden={locked ? "true" : undefined}
            >
              · {location}
            </span>
          )}
        </span>
        <span className="whitespace-nowrap">
          ·{" "}
          {locked
            ? formatMonth(c.contracting_publicatedat)
            : formatDate(c.contracting_publicatedat)}
        </span>
      </div>

      {/* Las coincidencias son la oportunidad y el motivo por el que la
          licitación está en la lista: no se esconde ninguna, pero una compra de
          ferretería trae hasta 31 y el resto queda a un clic. */}
      {matches.length > 0 && (
        <div className="flex flex-col gap-1.5 rounded-xl bg-emerald-500/5 p-3">
          {/* <div className="flex items-baseline justify-between gap-2">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
              Hizo match con tu perfil
            </span>
            <span className="shrink-0 text-xs font-extrabold tabular-nums text-emerald-700 dark:text-emerald-400">
              {formatMoney(matchTotal, currencySymbol)}
            </span>
          </div> */}
          {(showAllMatches ? matches : matches.slice(0, VISIBLE_MATCHES)).map(
            (m, i) => (
              <MatchRow
                key={`${m.description}-${i}`}
                match={m}
                currencySymbol={currencySymbol}
                isMinor={!!c.contracting_minorid}
              />
            ),
          )}
          {matches.length > VISIBLE_MATCHES && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowAllMatches((value) => !value);
              }}
              className="self-start text-[11px] font-bold text-emerald-700 hover:underline dark:text-emerald-400"
            >
              {showAllMatches
                ? "Ver menos"
                : `Ver ${matches.length - VISIBLE_MATCHES} más`}
            </button>
          )}
        </div>
      )}

      <div className="flex items-baseline justify-between gap-2 border-t pt-3">
        <span
          className="text-[11px] text-muted-foreground"
          title={amount.fromBases ? FROM_BASES_HINT : undefined}
        >
          Valor referencial{amount.fromBases && " · bases"}
        </span>
        {locked ? (
          <span
            className="select-none text-sm font-bold tabular-nums text-slate-400 blur-[5px]"
            aria-hidden="true"
          >
            {currencySymbol} 0,000,000
          </span>
        ) : (
          <span className="text-sm font-bold tabular-nums">
            {amount.amount === null
              ? "-"
              : formatMoney(amount.amount, currencySymbol)}
          </span>
        )}
      </div>

      {c.qualification && (
        <QualificationBadge qualification={c.qualification} className="self-end" />
      )}

      {locked && <RemainingUnlocks />}
    </div>
  );
}
